import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Grid, CircularProgress } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { SmartModuleType, TimeSeriesInfo } from '@moderepo/mode-internal-apis';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { fetchAllHomeSmartModules, fetchAllTimeSeriesInfo, userAppDataContext, selectAllHomeSmartModules, selectAllHomeTimeSeries,
    selectHomeSmartModuleByType, selectDeviceKVStore, fetchDeviceKVStore, selectHomeKVStore, fetchHomeKVStore }
    from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import * as SCUtils from './SCUtils';
import { SimpleSeriesMetricsDataProvider, SimpleSeriesMetricsDataProviderProps } from '../time-series/SimpleSeriesMetricsDataProvider';
import { SCSensorModuleInfoContainer } from './SCSensorModuleInfoContainer';
import { SCSensorsTableContainer } from './SCSensorsTableContainer';
import { useGetURLParamEntityIdAsNumber, useGetURLParamEntityIdAsString } from '../../utils/utils';



/**
 * This is the page where we show details of a Sensor Module.
 * NOTE: This page is used for displaying both Home's sensor module and Device sensor module. We will use the params in the page's path
 * to determine whether the user is viewing a Home or device's sensor module. If the path contains only a homeId then we will assume the
 * user is viewing a home's sensor module. If the path contains deviceId then we will assume the user is viewing a device's sensor module.
 */
export const SCSensorModuleDetailsPage: React.FC = () => {

    // get all the params from the URL
    const params = useParams();
    const projectId = Number(params.projectId);
    const moduleId = useGetURLParamEntityIdAsString('moduleId');
    const homeId = useGetURLParamEntityIdAsNumber('homeId');
    const deviceId = useGetURLParamEntityIdAsNumber('deviceId');

    const smContainer = deviceId ? SCUtils.SensorModuleContainer.DEVICE : SCUtils.SensorModuleContainer.HOME;
    const smContainerId = Number(deviceId || homeId);

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, projectId);
    const { trans } = useRitzTranslation(project);


    // The key/value pair used for configuring this sensor module
    const sensorModuleKVStore = useMemo(() => {
        if (!moduleId) {
            return undefined;
        }

        const key = SCUtils.getSensorModuleKVStoreKey(moduleId);
        const kvPair = smContainer === SCUtils.SensorModuleContainer.DEVICE
            ? selectDeviceKVStore(appDataState, smContainerId, key)
            : selectHomeKVStore(appDataState, smContainerId, key);

        if (key) {
            // convert KeyValuePair to SensorCloudKeyValuePair
            return kvPair as SCUtils.SensorCloudKeyValuePair;
        }
        return undefined;
    }, [appDataState, moduleId, smContainer, smContainerId]);


    const sensorModule = useMemo(() => {
        if (sensorModuleKVStore) {
            return SCUtils.fixSensorModuleData(sensorModuleKVStore.value);
        }
        return undefined;
    }, [sensorModuleKVStore]);


    const device = sensorModule ? selectProjectDeviceById(dataState, Number(sensorModule.gatewayId)) : undefined;


    // The TSDB smart module that is setup for the device's home. Not all project has TSDB smart module setup so this can be undefined.
    const isSmartModulesLoaded = device && selectAllHomeSmartModules(appDataState, device.homeId) !== undefined;
    const tsdbSmartModule = device ? selectHomeSmartModuleByType(appDataState, device.homeId, SmartModuleType.TSDB) : undefined;

    // Get all the home's time series info from the data state
    const allHomeTimeSeriesInfo = device && tsdbSmartModule ? selectAllHomeTimeSeries(appDataState, device.homeId, tsdbSmartModule.id) : undefined;

    // The child's action that needs to be shown in the page's title bar as the main action.
    // This is a way for the page to show a UI component created and controlled by a child components
    const [pageMainAction, setPageMainAction] = useState<JSX.Element | undefined>();



    /**
     * This useEffect is used for loading device data on component load
     */
    useEffect(() => {
        if (sensorModule && !device) {
            dataDispatch(fetchProjectDeviceById(projectId, Number(sensorModule.gatewayId)));
        }
    }, [dataDispatch, device, deviceId, projectId, sensorModule]);



    /**
     * This useEffect is used for loading sensor module's key/value pair on component load
     */
    useEffect(() => {
        if (!moduleId) {
            return;
        }
        const key = SCUtils.getSensorModuleKVStoreKey(moduleId);

        if (smContainer === SCUtils.SensorModuleContainer.DEVICE) {
            appDataDispatch(fetchDeviceKVStore(smContainerId, key)).catch(() => {
                /* ignore error */
            });
        } else {
            appDataDispatch(fetchHomeKVStore(smContainerId, key)).catch(() => {
                /* ignore error */
            });
        }
    }, [appDataDispatch, moduleId, smContainer, smContainerId]);



    /**
     * This use effect is used for fetching smartModules on component load if smart modules are not loaded.
     */
    useEffect(() => {
        if (!isSmartModulesLoaded && device) {
            try {
                // If a project does not have TSDB setup or a home does not have smart modules, this will throw an error. For this case, we don't
                // need to throw error. We can just show no data message instead.
                appDataDispatch(fetchAllHomeSmartModules(device.homeId, true, []));
            } catch {
                // ignore error
            }
        }
    }, [appDataDispatch, device, isSmartModulesLoaded]);



    /**
     * Build a list of timeSeriesIds for the sensor module. We will display charts for these timeSeriesIds
     */
    const timeSeriesIdsInfo = useMemo((): SimpleSeriesMetricsDataProviderProps['timeSeriesIds'] | undefined => {
        if (device && tsdbSmartModule && sensorModuleKVStore && sensorModule && allHomeTimeSeriesInfo) {

            // NOTE: seriesInfo contains all time series for a home including time series for other sensor module
            // and also for sensors that are no longer available for the sensor module because they might be removed.
            // So we need to filter out series that don't belong to this sensor module. Time series info does not
            // have any property that tell us which sensor module it belonged to. However, time series info's id
            // is consists of ${sensorModuleId}-${sensorName}. So we can match time series with sensor module by matching
            // the first part of time series id with the sensor module id
            const sensorValueNames = SCUtils.getAvailableSensorsList(sensorModule);

            const filteredTimeSeriesInfo = allHomeTimeSeriesInfo.filter(
                (series: TimeSeriesInfo): boolean => {
                    const { sensorModuleId, sensorValueName } = SCUtils.parseTimeSeriesId(series.id);
                    return sensorModule.id.toLocaleUpperCase() === sensorModuleId.toUpperCase()
                            && sensorValueNames.find((s: string): boolean => {
                                // return true is one of the sensors in the sensor module is the same as the sensor name in the seriesId.
                                return s.toUpperCase() === sensorValueName;
                            }) !== undefined;
                },
            );

            return filteredTimeSeriesInfo.map((timeSeries: TimeSeriesInfo) => {
                const { sensorValueName } = SCUtils.parseTimeSeriesId(timeSeries.id);
                // check if this sensor module has a type defined in the catalog
                const sensorModuleType = SCUtils.sensorModuleCatalogMap.get(sensorModuleKVStore.value.modelId);
                const sensorDisplayNames = sensorModuleType ? sensorModuleType.moduleSchemaDisplayNames : undefined;

                return {
                    seriesId   : timeSeries.id,
                    valueName  : sensorValueName,
                    displayName: sensorDisplayNames ? sensorDisplayNames[sensorValueName] : undefined,
                };
            });
        }

        return undefined;
    }, [allHomeTimeSeriesInfo, device, sensorModule, sensorModuleKVStore, tsdbSmartModule]);


    /**
     * This useEffect is used for loading home's time series info on component load. This will only load
     * time series info but not data.
     */
    useEffect(() => {
        if (device && tsdbSmartModule && sensorModuleKVStore && !timeSeriesIdsInfo) {
            appDataDispatch(fetchAllTimeSeriesInfo(device.homeId, tsdbSmartModule.id), true, true).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [device, tsdbSmartModule, sensorModuleKVStore, appDataDispatch, timeSeriesIdsInfo]);



    const isFetchingData = Boolean(
        !project
        || !device
        || !sensorModuleKVStore
        || !isSmartModulesLoaded
        || !timeSeriesIdsInfo,
    );


    return (
        <div className={clsx('page-main-content')}>
            {!isFetchingData && moduleId && sensorModuleKVStore && (
                <>
                    <div className="title-bar">
                        <div className="title-bar-item title-text">
                            <div className="page-title">
                                {trans('page.title.sensor_module_details')}
                            </div>
                            <div className="page-subtitle">
                                {trans('page.subtitle.sensor_module_details')}
                            </div>
                        </div>
                        {pageMainAction}
                    </div>


                    <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                            <SCSensorModuleInfoContainer
                                className="match-height"
                                projectId={projectId}
                                moduleId={moduleId}
                                smContainer={smContainer}
                                smContainerId={smContainerId}
                            />
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <SCSensorsTableContainer
                                className="match-height"
                                projectId={projectId}
                                smContainer={smContainer}
                                smContainerId={smContainerId}
                                moduleId={moduleId}
                            />
                        </Grid>

                        {device && timeSeriesIdsInfo && (
                            <Grid item xs={12}>
                                <SimpleSeriesMetricsDataProvider
                                    projectId={projectId}
                                    deviceId={deviceId}
                                    timeSeriesIds={timeSeriesIdsInfo}
                                    homeId={device.homeId}
                                    tsdbSmartModuleId={tsdbSmartModule?.id}
                                    setPageMainAction={setPageMainAction}
                                />
                            </Grid>
                        )}
                    </Grid>
                </>
            )}


            {isFetchingData && (
                <CircularProgress />
            )}

        </div>
    );
};
